import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Cpu, Sun, CloudRain, RefreshCw, Search } from 'lucide-react';
import '../pages.css';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { LoadingState, EmptyState, ErrorState } from '../../components/ui/States';
import { useAuth, authHeaders } from '../../auth';
import { formatRelative } from '../../relativeTime';

const API_URL = process.env.REACT_APP_API_URL || '';
const API_BASE = `${API_URL}/api`;
// Fallback reconciliation for status transitions the live socket doesn't
// cover (a device going OFFLINE has no "it stopped sending" event to push -
// see the Socket.IO note below). Matches the same cadence used elsewhere
// (Alerts page fallback poll).
const REFRESH_INTERVAL_MS = 30000;

export default function DevicesPage() {
  const { token } = useAuth();
  const { plants, socket } = useOutletContext();
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState(false);
  const [now, setNow] = useState(Date.now());

  const [plantFilter, setPlantFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const safePlants = Array.isArray(plants) ? plants : [];
  const plantMetaById = useMemo(() => {
    const map = new Map();
    safePlants.forEach((p) => map.set(p.plant_id, p));
    return map;
  }, [safePlants]);

  const load = async () => {
    setError(false);
    try {
      const res = await fetch(`${API_BASE}/admin/devices`, { headers: authHeaders(token) });
      if (!res.ok) throw new Error('load failed');
      setDevices(await res.json());
    } catch {
      setDevices((prev) => prev ?? []);
      setError(true);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [token]);

  // Keeps the relative-time labels ("Il y a 8 s") live without re-fetching.
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  // Real-time option (audited, low-risk): sensor_data/weather_data already
  // flow over the one shared socket for the live dashboard/plant pages, and
  // both payloads already carry plant_id + panel_id/station_id, matching
  // buildDeviceList()'s `id` format exactly - so a matching device's
  // last_seen/status can be updated in place the instant telemetry arrives,
  // with no new connection and no new backend field. This only ever moves a
  // device to ONLINE; an OFFLINE transition has no such event to push (the
  // backend's offline-check runs on its own interval), which is why the 30s
  // REST poll above is kept as the reconciliation path for that direction
  // rather than trying to mirror the offline timer client-side.
  useEffect(() => {
    if (!socket) return;
    const onSensorData = (data) => {
      const id = `${data.plant_id}-${data.panel_id}`;
      setDevices((prev) => (prev || []).map((d) => (d.id === id ? { ...d, status: 'online', last_seen: data.lastUpdate || new Date().toISOString() } : d)));
    };
    const onWeatherData = (payload) => {
      const { plant_id, station_id } = payload || {};
      if (!plant_id || !station_id) return;
      const id = `${plant_id}-${station_id}`;
      setDevices((prev) => (prev || []).map((d) => (d.id === id ? { ...d, status: 'online', last_seen: new Date().toISOString() } : d)));
    };
    socket.on('sensor_data', onSensorData);
    socket.on('weather_data', onWeatherData);
    return () => {
      socket.off('sensor_data', onSensorData);
      socket.off('weather_data', onWeatherData);
    };
  }, [socket]);

  const summary = useMemo(() => {
    const list = devices || [];
    return {
      total: list.length,
      online: list.filter((d) => d.status === 'online').length,
      offline: list.filter((d) => d.status === 'offline').length,
      panels: list.filter((d) => d.type === 'panel').length,
      weather: list.filter((d) => d.type === 'weather').length
    };
  }, [devices]);

  const filtered = useMemo(() => {
    let list = devices || [];
    if (plantFilter !== 'all') list = list.filter((d) => d.plant_id === plantFilter);
    if (typeFilter !== 'all') list = list.filter((d) => d.type === typeFilter);
    if (statusFilter !== 'all') list = list.filter((d) => d.status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((d) =>
        (d.id || '').toLowerCase().includes(q) ||
        (d.panel_id || '').toLowerCase().includes(q) ||
        (d.station_id || '').toLowerCase().includes(q) ||
        (d.mac || '').toLowerCase().includes(q)
      );
    }
    // Offline devices first (the ones that actually need attention), most
    // recently seen first within each group.
    return [...list].sort((a, b) => {
      if (a.status !== b.status) return a.status === 'offline' ? -1 : 1;
      return new Date(b.last_seen) - new Date(a.last_seen);
    });
  }, [devices, plantFilter, typeFilter, statusFilter, search]);

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Tableau de bord', to: '/' }, { label: 'Appareils' }]} />
      <div className="page-header">
        <h1><Cpu size={22} /> Appareils</h1>
        <button className="btn-secondary" onClick={load}><RefreshCw size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Actualiser</button>
      </div>

      {devices === null ? (
        <LoadingState label="Chargement des appareils..." />
      ) : error && devices.length === 0 ? (
        <ErrorState label="Impossible de charger les appareils" />
      ) : (
        <>
          <div className="summary-grid summary-grid-compact" style={{ marginBottom: 16 }}>
            <div className="summary-card">
              <div className="summary-label">Total</div>
              <div className="summary-value">{summary.total}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">En ligne</div>
              <div className="summary-value">{summary.online}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Hors ligne</div>
              <div className="summary-value">{summary.offline}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Panneaux</div>
              <div className="summary-value">{summary.panels}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Stations météo</div>
              <div className="summary-value">{summary.weather}</div>
            </div>
          </div>

          <div className="alert-filter-bar">
            <select value={plantFilter} onChange={(e) => setPlantFilter(e.target.value)}>
              <option value="all">Toutes les centrales</option>
              {safePlants.map((p) => <option key={p.plant_id} value={p.plant_id}>{p.name || p.plant_id}</option>)}
            </select>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">Tous types</option>
              <option value="panel">Panneau solaire</option>
              <option value="weather">Station météo</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Tous statuts</option>
              <option value="online">En ligne</option>
              <option value="offline">Hors ligne</option>
            </select>
            <div className="alert-search-box">
              <Search size={14} />
              <input type="text" placeholder="Rechercher (id, MAC...)" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>

          {filtered.length === 0 ? (
            <EmptyState label={devices.length === 0 ? 'Aucun appareil détecté' : 'Aucun appareil ne correspond aux filtres'} />
          ) : (
            <div className="devices-grid">
              {filtered.map((d) => {
                const meta = plantMetaById.get(d.plant_id);
                const plantName = meta?.name || d.plant_id;
                const showPlantId = plantName !== d.plant_id;
                const isOnline = d.status === 'online';
                return (
                  <div key={d.id} className={`device-card device-card-${isOnline ? 'online' : 'offline'}`}>
                    <div className="device-card-top">
                      <span className="device-card-name">
                        {d.type === 'panel' ? <Sun size={16} /> : <CloudRain size={16} />}
                        {d.label}
                      </span>
                      <span className={`device-status-pill device-status-pill-${isOnline ? 'online' : 'offline'}`}>
                        <span className={`status-dot status-dot-${isOnline ? 'online' : 'offline'}`} style={{ margin: 0 }}></span>
                        {isOnline ? 'EN LIGNE' : 'HORS LIGNE'}
                      </span>
                    </div>
                    <div className="device-card-type">{d.typeLabel}</div>

                    <div className="device-card-row">
                      <span className="device-card-plant-name">{plantName}</span>
                      {showPlantId && <span className="device-card-plant-id"> ({d.plant_id})</span>}
                      {' · '}
                      {d.panel_id ? `Panneau ${d.panel_id}` : `Station ${d.station_id}`}
                    </div>
                    <div className="device-card-row device-card-mac">MAC : {d.mac || 'Inconnu'}</div>

                    <div className="device-card-footer">
                      <span className="device-card-relative-time">{formatRelative(d.last_seen, now)}</span>
                      <span className="device-card-exact-time">{new Date(d.last_seen).toLocaleString('fr-FR')}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
